import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import { getCurrentUserRole, getAllowedSidebarPages } from "../utils/roleAccess";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import "../assets/css/DigixLeads.css";
import "../assets/css/Projects.css";
import "../assets/css/Dashboard.css";
import MyDay from "./MyDay";
import DMCalendar from "./DMCalendar";

const CARD_KEY_TO_PAGE = {
  projects: "Projects",
  digixLeads: "DigixLeads",
  customers: "Tenants",
  accounts: "Accounts",
  issues: "Issues",
};

function toISODateLocal(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

const DIGIX_MODULE_DEFS = [
  { key: "business", title: "Business Leads", path: "/business-leads" },
  { key: "realestate", title: "Real Estate Leads", path: "/realestate-leads" },
  { key: "training", title: "Training Leads", path: "/training-leads" },
  { key: "job", title: "Job Leads", path: "/job-leads" },
  { key: "internship", title: "Internship Leads", path: "/internship-leads" },
];

function emptyDigixModules() {
  return DIGIX_MODULE_DEFS.map((d) => ({
    key: d.key,
    title: d.title,
    path: d.path,
    total: 0,
    newLeads: 0,
    newLeadsToday: 0,
    waiting: 0,
    nextFollowup: 0,
    followup: 0,
    followupToday: 0,
  }));
}

function toNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function mergeDigixModules(modules) {
  const byKey = new Map((Array.isArray(modules) ? modules : []).map((m) => [m?.key, m]));
  return emptyDigixModules().map((base) => {
    const src = byKey.get(base.key) || {};
    return {
      ...base,
      total: toNum(src.total),
      newLeads: toNum(src.newLeads),
      newLeadsToday: toNum(src.newLeadsToday),
      waiting: toNum(src.waiting),
      nextFollowup: toNum(src.nextFollowup),
      followup: toNum(src.followup),
      followupToday: toNum(src.followupToday),
    };
  });
}

let dashboardCountsInFlight = null;

function fetchDashboardCounts() {
  if (!dashboardCountsInFlight) {
    dashboardCountsInFlight = axiosInstance
      .get("/dashboard/counts")
      .finally(() => {
        dashboardCountsInFlight = null;
      });
  }
  return dashboardCountsInFlight;
}

/** Status labels passed to DigixLeads via navigation state (must match /lead-statuses and lead row status text, compared case-insensitively). */
const DIGIX_BUCKET_TO_STATUS = {
  newLeads: "New",
  newLeadsToday: "New",
  waiting: "Waiting for Confirmation",
  followup: "Followup",
  followupToday: "Followup",
  nextFollowup: "Next Followup",
  total: "All Statuses",
};

function digixNavigateState(bucket) {
  const status = DIGIX_BUCKET_TO_STATUS[bucket] ?? "All Statuses";
  const today = toISODateLocal();
  if (bucket === "newLeadsToday") {
    return {
      digixFilter: {
        status,
        datePreset: "today",
        createdFrom: today,
        createdTo: today,
        nextFollowupDate: "",
      },
    };
  }
  if (bucket === "followupToday") {
    return {
      digixFilter: {
        status: "All Statuses",
        datePreset: "all",
        createdFrom: "",
        createdTo: "",
        nextFollowupDate: today,
      },
    };
  }
  return {
    digixFilter: {
      status,
      datePreset: "all",
      createdFrom: "",
      createdTo: "",
      nextFollowupDate: "",
    },
  };
}

function DigixStatusCountBtn({ count, tone, onClick, showZero = false }) {
  const n = Number(count) || 0;
  const isEmpty = n === 0 && !showZero;
  return (
    <button
      type="button"
      className={
        isEmpty
          ? "dashboard-digix-cell-btn dashboard-digix-status-empty"
          : n === 0
            ? "dashboard-digix-cell-btn dashboard-digix-status-zero"
            : `dashboard-digix-cell-btn dashboard-digix-status-pill dashboard-digix-status-pill--${tone}`
      }
      onClick={onClick}
    >
      {isEmpty ? "–" : n}
    </button>
  );
}

function DigixDualCount({ todayCount, allCount, tone, onToday, onAll }) {
  const all = Number(allCount) || 0;
  if (all === 0) {
    return (
      <DigixStatusCountBtn count={0} tone={tone} onClick={onAll} />
    );
  }
  return (
    <div className="dashboard-digix-dual">
      <DigixStatusCountBtn count={todayCount} tone={tone} onClick={onToday} showZero />
      <span className="dashboard-digix-dual__sep" aria-hidden>/</span>
      <DigixStatusCountBtn count={allCount} tone={tone} onClick={onAll} />
    </div>
  );
}

function DashboardDigixTable({ modules, navigate }) {
  return (
    <div className="dashboard-digix-table-card">
      <div className="dashboard-digix-table-card__header">
        <h3 className="dashboard-digix-table-card__title">Digix Leads</h3>
      </div>

      <div className="dashboard-digix-table-wrap">
        <table className="dashboard-digix-table">
            <thead>
              <tr>
                <th className="dashboard-digix-table__th dashboard-digix-table__th--module">Module</th>
                <th className="dashboard-digix-table__th dashboard-digix-table__th--status dashboard-digix-table__th--new">
                  New leads
                  <span className="dashboard-digix-th-sub">Today / All</span>
                </th>
                <th className="dashboard-digix-table__th dashboard-digix-table__th--status dashboard-digix-table__th--waiting">
                  Waiting for confirmation
                </th>
                <th className="dashboard-digix-table__th dashboard-digix-table__th--status dashboard-digix-table__th--followup">
                  Followup
                  <span className="dashboard-digix-th-sub">Today / All</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {modules.map((mod) => (
                <tr key={mod.key} className="dashboard-digix-table__row">
                  <td className="dashboard-digix-table__td dashboard-digix-table__td--module">
                    <button
                      type="button"
                      className="dashboard-digix-cell-btn dashboard-digix-cell-btn--module"
                      onClick={() => navigate(mod.path, { state: digixNavigateState("total") })}
                    >
                      {mod.title}
                    </button>
                  </td>
                  <td className="dashboard-digix-table__td dashboard-digix-table__td--num">
                    <DigixDualCount
                      todayCount={mod.newLeadsToday}
                      allCount={mod.newLeads}
                      tone="new"
                      onToday={() => navigate(mod.path, { state: digixNavigateState("newLeadsToday") })}
                      onAll={() => navigate(mod.path, { state: digixNavigateState("newLeads") })}
                    />
                  </td>
                  <td className="dashboard-digix-table__td dashboard-digix-table__td--num">
                    <DigixStatusCountBtn
                      count={mod.waiting}
                      tone="waiting"
                      onClick={() => navigate(mod.path, { state: digixNavigateState("waiting") })}
                    />
                  </td>
                  <td className="dashboard-digix-table__td dashboard-digix-table__td--num">
                    <DigixDualCount
                      todayCount={mod.followupToday}
                      allCount={mod.followup}
                      tone="followup"
                      onToday={() => navigate(mod.path, { state: digixNavigateState("followupToday") })}
                      onAll={() => navigate(mod.path, { state: digixNavigateState("followup") })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
    </div>
  );
}

function DashboardCollapsibleTableCard({ title, cardClassName, children, collapsible = true }) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className={cardClassName}>
      <div className="dashboard-digix-table-card__header">
        <h3 className="dashboard-digix-table-card__title">{title}</h3>
        {collapsible ? (
          <button
            type="button"
            className="dashboard-digix-table-card__collapse-btn"
            aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((v) => !v)}
          >
            <span className="dashboard-digix-table-card__collapse-icon">
              {collapsed ? <FaChevronDown /> : <FaChevronUp />}
            </span>
          </button>
        ) : null}
      </div>
      {!collapsible || !collapsed ? children : null}
    </div>
  );
}

function DashboardTasksOverviewTable({ rows, navigate }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return <div className="dashboard-customer-projects-empty">No task data found.</div>;
  }

  const openTask = (row) => {
    const preset = row?.statusBucket === "inProgress" ? "inProgress" : "todo";
    navigate("/tasks", {
      state: { dashboardTaskStatusPreset: preset },
    });
  };

  const openSubTask = (row) => {
    const pid = row?.projectId;
    const sid = row?.storyId;
    const tid = row?.taskId;
    const stid = row?.subTaskId;
    if (pid && sid && tid && stid) {
      if (row?.subProjectId) {
        navigate(`/projects/${pid}/subprojects/${row.subProjectId}/user-stories/${sid}/tasks/${tid}/subtasks/${stid}/view`);
      } else {
        navigate(`/projects/${pid}/user-stories/${sid}/tasks/${tid}/subtasks/${stid}/view`);
      }
      return;
    }
    if (pid && sid && tid) {
      if (row?.subProjectId) {
        navigate(`/projects/${pid}/subprojects/${row.subProjectId}/user-stories/${sid}/tasks/${tid}/view`);
      } else {
        navigate(`/projects/${pid}/user-stories/${sid}/tasks/${tid}/view`);
      }
      return;
    }
    openTask(row);
  };

  const activateRow = (row) => {
    if (row.type === "Subtask") openSubTask(row);
    else openTask(row);
  };

  return (
    <div className="dashboard-customer-projects-wrap">
      <table className="dashboard-digix-table dashboard-customer-projects-table">
        <thead>
          <tr>
            <th className="dashboard-digix-table__th dashboard-digix-table__th--module">Title</th>
            <th className="dashboard-digix-table__th">Type</th>
            <th className="dashboard-digix-table__th">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              className="dashboard-digix-table__row dashboard-digix-table__row--clickable"
              tabIndex={0}
              onClick={() => activateRow(row)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  activateRow(row);
                }
              }}
              aria-label={
                row.type === "Subtask"
                  ? `Open subtask ${row.title}`
                  : `Open task ${row.title}`
              }
            >
              <td className="dashboard-digix-table__td dashboard-digix-table__td--module">{row.title}</td>
              <td className="dashboard-digix-table__td dashboard-digix-table__td--num">{row.type}</td>
              <td className="dashboard-digix-table__td dashboard-digix-table__td--num">{row.statusLabel}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DashboardIssuesOverviewTable({ counts, navigate }) {
  const openCount = Number(counts?.open ?? 0);
  const inProgressCount = Number(counts?.inProgress ?? 0);
  const reopenedCount = Number(counts?.reopened ?? 0);

  const goToIssues = (preset) => {
    navigate("/issues", { state: { dashboardIssueStatusPreset: preset } });
  };

  return (
    <div className="dashboard-customer-projects-wrap">
      <table className="dashboard-digix-table dashboard-customer-projects-table">
        <thead>
          <tr>
            <th className="dashboard-digix-table__th dashboard-digix-table__th--module">Type</th>
            <th className="dashboard-digix-table__th">Open</th>
            <th className="dashboard-digix-table__th">In Progress</th>
            <th className="dashboard-digix-table__th">Reopened</th>
          </tr>
        </thead>
        <tbody>
          <tr className="dashboard-digix-table__row">
            <td className="dashboard-digix-table__td dashboard-digix-table__td--module">Issues</td>
            <td className="dashboard-digix-table__td dashboard-digix-table__td--num">
              <button type="button" className="dashboard-digix-cell-btn" onClick={() => goToIssues("open")}>
                {openCount}
              </button>
            </td>
            <td className="dashboard-digix-table__td dashboard-digix-table__td--num">
              <button type="button" className="dashboard-digix-cell-btn" onClick={() => goToIssues("inProgress")}>
                {inProgressCount}
              </button>
            </td>
            <td className="dashboard-digix-table__td dashboard-digix-table__td--num">
              <button type="button" className="dashboard-digix-cell-btn" onClick={() => goToIssues("reopened")}>
                {reopenedCount}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const role = getCurrentUserRole();
  const isAdmin = useMemo(() => normalizeText(role) === "admin", [role]);
  const isDeveloper = useMemo(() => normalizeText(role).includes("developer"), [role]);
  const isTester = useMemo(() => normalizeText(role).includes("tester"), [role]);
  const isDevops = useMemo(() => normalizeText(role).includes("devops"), [role]);
  const isHr = useMemo(() => {
    const r = normalizeText(role);
    if (!r) return false;
    if (r.includes("human resource")) return true;
    const tokens = r.split(/[\s/_-]+/).filter(Boolean);
    if (tokens.includes("hr")) return true;
    if (tokens.some((t) => t.startsWith("hr-") || t.endsWith("-hr"))) return true;
    return false;
  }, [role]);
  /** HR-style dashboard: HR role name, or HR Management module in sidebar (and not dev/tester/devops/admin layout). */
  const showHrDashboard = useMemo(() => {
    if (isAdmin || isDeveloper || isTester || isDevops) return false;
    if (isHr) return true;
    return getAllowedSidebarPages(role).includes("HRManagement");
  }, [isAdmin, isDeveloper, isTester, isDevops, isHr, role]);
  const isBusinessAnalyst = useMemo(() => {
    const r = normalizeText(role);
    if (!r) return false;
    return r.includes("business analyst") || r.includes("businessanalyst");
  }, [role]);
  const showBaDashboard = useMemo(() => {
    if (isAdmin || isDeveloper || isTester || isDevops || showHrDashboard) return false;
    return isBusinessAnalyst;
  }, [isAdmin, isDeveloper, isTester, isDevops, showHrDashboard, isBusinessAnalyst]);
  const isFinanceTeam = useMemo(() => {
    const r = normalizeText(role);
    if (!r) return false;
    return (
      r.includes("finance") ||
      r.includes("accountant") ||
      r.includes("accounts receivable") ||
      r.includes("bookkeeper") ||
      r.includes("treasury")
    );
  }, [role]);
  const showFinanceDashboard = useMemo(() => {
    if (isAdmin || isDeveloper || isTester || isDevops || showHrDashboard || showBaDashboard) return false;
    return isFinanceTeam;
  }, [isAdmin, isDeveloper, isTester, isDevops, showHrDashboard, showBaDashboard, isFinanceTeam]);
  const isSalesTeam = useMemo(() => {
    const r = normalizeText(role);
    if (!r) return false;
    return (
      r.includes("sales") ||
      r.includes("relationship manager") ||
      r.includes("business development") ||
      r === "bdm" ||
      r.includes("account executive")
    );
  }, [role]);
  const showSalesDashboard = useMemo(() => {
    if (
      isAdmin ||
      isDeveloper ||
      isTester ||
      isDevops ||
      showHrDashboard ||
      showBaDashboard ||
      showFinanceDashboard
    ) {
      return false;
    }
    return isSalesTeam;
  }, [
    isAdmin,
    isDeveloper,
    isTester,
    isDevops,
    showHrDashboard,
    showBaDashboard,
    showFinanceDashboard,
    isSalesTeam,
  ]);
  const allowedPages = getAllowedSidebarPages(role);
  const quickLinks = [
    { key: "projects", label: "Projects", path: "/projects" },
    { key: "customers", label: "Tenants", path: "/tenants" },
    { key: "issues", label: "Issue Tracker", path: "/issues" },
  ];
  const [counts, setCounts] = useState({
    projects: null,
    digixLeads: null,
    customers: null,
    accounts: null,
    issues: null,
  });
  const [adminStats, setAdminStats] = useState({
    projectsNew: 0,
    projectsInProgress: 0,
    projectsActive: 0,
    projectsCompleted: 0,
    tasksTodo: 0,
    tasksInProgress: 0,
    subtasksTodo: 0,
    subtasksInProgress: 0,
    issuesOpen: 0,
    issuesInProgress: 0,
    issuesReopened: 0,
    digixModules: emptyDigixModules(),
    customersTotal: 0,
    followupsToday: 0,
    followupsOverdue: 0,
  });
  const [workTab, setWorkTab] = useState("my-day");
  const [roleTasksCardExpanded, setRoleTasksCardExpanded] = useState(false);
  const [roleTasksOverviewRows, setRoleTasksOverviewRows] = useState([]);

  const [developerStats, setDeveloperStats] = useState({
    assignedProjects: 0,
    assignedTasksTodo: 0,
    assignedTasksInProgress: 0,
    assignedIssuesOpen: 0,
    assignedIssuesInProgress: 0,
    assignedIssuesReopened: 0,
    assignedIssuesTotal: 0,
    assignedIssuesResolved: 0,
  });
  const [testerStats, setTesterStats] = useState({
    assignedProjects: 0,
    assignedTasksTodo: 0,
    assignedTasksInProgress: 0,
    myIssuesOpen: 0,
    myIssuesInProgress: 0,
    myIssuesReopened: 0,
    myIssuesTotal: 0,
    myIssuesResolved: 0,
  });
  const [devopsStats, setDevopsStats] = useState({
    assignedProjects: 0,
    assignedTasksTodo: 0,
    assignedTasksInProgress: 0,
    assignedIssuesOpen: 0,
    assignedIssuesInProgress: 0,
    assignedIssuesReopened: 0,
    assignedIssuesTotal: 0,
    assignedIssuesResolved: 0,
    myIssuesOpen: 0,
    myIssuesInProgress: 0,
    myIssuesReopened: 0,
    myIssuesTotal: 0,
    myIssuesResolved: 0,
  });
  const [hrStats, setHrStats] = useState({
    assignedTasksTodo: 0,
    assignedTasksInProgress: 0,
    assignedIssuesOpen: 0,
    assignedIssuesInProgress: 0,
    assignedIssuesReopened: 0,
    assignedIssuesTotal: 0,
  });

  const [developerIssuesCardExpanded, setDeveloperIssuesCardExpanded] = useState(false);
  const [testerIssuesCardExpanded, setTesterIssuesCardExpanded] = useState(false);
  const [hrIssuesCardExpanded, setHrIssuesCardExpanded] = useState(false);
  const [financeIssuesCardExpanded, setFinanceIssuesCardExpanded] = useState(false);
  const [salesIssuesCardExpanded, setSalesIssuesCardExpanded] = useState(false);
  const [devopsAssignedIssuesCardExpanded, setDevopsAssignedIssuesCardExpanded] = useState(false);
  const [devopsMyIssuesCardExpanded, setDevopsMyIssuesCardExpanded] = useState(false);

  const [baStats, setBaStats] = useState({
    activeProjects: 0,
    createdUserStories: 0,
  });
  const [financeStats, setFinanceStats] = useState({
    assignedTasksTodo: 0,
    assignedTasksInProgress: 0,
    assignedIssuesOpen: 0,
    assignedIssuesInProgress: 0,
    assignedIssuesReopened: 0,
    assignedIssuesTotal: 0,
    paymentsCompleted: 0,
    paymentsDue: 0,
  });
  const [salesStats, setSalesStats] = useState({
    assignedTasksTodo: 0,
    assignedTasksInProgress: 0,
    assignedIssuesOpen: 0,
    assignedIssuesInProgress: 0,
    assignedIssuesReopened: 0,
    assignedIssuesTotal: 0,
    digixModules: emptyDigixModules(),
  });
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await fetchDashboardCounts();
        const data = res?.data || {};
        if (!mounted) return;
        const projects = data.projects || {};
        const tasks = data.tasks || {};
        const subtasks = data.subtasks || {};
        const issues = data.issues || {};
        const assignedIssues = issues.assigned || {};
        const createdIssues = issues.created || {};
        const leads = data.leads || {};
        const customers = data.customers || {};
        const accounts = data.accounts || {};
        const userStories = data.userStories || {};
        const payments = data.payments || {};
        const digixModules = mergeDigixModules(leads.modules);
        const followups = data.followups || {};

        setCounts({
          projects: toNum(projects.total),
          digixLeads: toNum(leads.total),
          customers: toNum(customers.total),
          accounts: toNum(accounts.total),
          issues: toNum(issues.total),
        });
        setAdminStats({
          projectsNew: toNum(projects.new),
          projectsInProgress: toNum(projects.active),
          projectsActive: toNum(projects.active),
          projectsCompleted: toNum(projects.completed),
          tasksTodo: toNum(tasks.todo),
          tasksInProgress: toNum(tasks.inProgress),
          subtasksTodo: toNum(subtasks.todo),
          subtasksInProgress: toNum(subtasks.inProgress),
          issuesOpen: toNum(issues.open),
          issuesInProgress: toNum(issues.inProgress),
          issuesReopened: toNum(issues.reopened),
          digixModules,
          customersTotal: toNum(customers.total),
          followupsToday: toNum(followups.today),
          followupsOverdue: toNum(followups.overdue),
        });
        setDeveloperStats({
          assignedProjects: toNum(projects.total),
          assignedTasksTodo: toNum(tasks.assignedTodo),
          assignedTasksInProgress: toNum(tasks.assignedInProgress),
          assignedIssuesOpen: toNum(assignedIssues.open),
          assignedIssuesInProgress: toNum(assignedIssues.inProgress),
          assignedIssuesReopened: toNum(assignedIssues.reopened),
          assignedIssuesTotal: toNum(assignedIssues.total),
          assignedIssuesResolved: toNum(assignedIssues.resolved),
        });
        setTesterStats({
          assignedProjects: toNum(projects.total),
          assignedTasksTodo: toNum(tasks.assignedTodo),
          assignedTasksInProgress: toNum(tasks.assignedInProgress),
          myIssuesOpen: toNum(createdIssues.open),
          myIssuesInProgress: toNum(createdIssues.inProgress),
          myIssuesReopened: toNum(createdIssues.reopened),
          myIssuesTotal: toNum(createdIssues.total),
          myIssuesResolved: toNum(createdIssues.resolved),
        });
        setDevopsStats({
          assignedProjects: toNum(projects.total),
          assignedTasksTodo: toNum(tasks.assignedTodo),
          assignedTasksInProgress: toNum(tasks.assignedInProgress),
          assignedIssuesOpen: toNum(assignedIssues.open),
          assignedIssuesInProgress: toNum(assignedIssues.inProgress),
          assignedIssuesReopened: toNum(assignedIssues.reopened),
          assignedIssuesTotal: toNum(assignedIssues.total),
          assignedIssuesResolved: toNum(assignedIssues.resolved),
          myIssuesOpen: toNum(createdIssues.open),
          myIssuesInProgress: toNum(createdIssues.inProgress),
          myIssuesReopened: toNum(createdIssues.reopened),
          myIssuesTotal: toNum(createdIssues.total),
          myIssuesResolved: toNum(createdIssues.resolved),
        });
        setHrStats({
          assignedTasksTodo: toNum(tasks.assignedTodo),
          assignedTasksInProgress: toNum(tasks.assignedInProgress),
          assignedIssuesOpen: toNum(assignedIssues.open),
          assignedIssuesInProgress: toNum(assignedIssues.inProgress),
          assignedIssuesReopened: toNum(assignedIssues.reopened),
          assignedIssuesTotal: toNum(assignedIssues.total),
        });
        setBaStats({
          activeProjects: toNum(projects.active),
          createdUserStories: toNum(userStories.created),
        });
        setFinanceStats({
          assignedTasksTodo: toNum(tasks.assignedTodo),
          assignedTasksInProgress: toNum(tasks.assignedInProgress),
          assignedIssuesOpen: toNum(assignedIssues.open),
          assignedIssuesInProgress: toNum(assignedIssues.inProgress),
          assignedIssuesReopened: toNum(assignedIssues.reopened),
          assignedIssuesTotal: toNum(assignedIssues.total),
          paymentsCompleted: toNum(payments.completed),
          paymentsDue: toNum(payments.due),
        });
        setSalesStats({
          assignedTasksTodo: toNum(tasks.assignedTodo),
          assignedTasksInProgress: toNum(tasks.assignedInProgress),
          assignedIssuesOpen: toNum(assignedIssues.open),
          assignedIssuesInProgress: toNum(assignedIssues.inProgress),
          assignedIssuesReopened: toNum(assignedIssues.reopened),
          assignedIssuesTotal: toNum(assignedIssues.total),
          digixModules,
        });
      } catch {
        if (!mounted) return;
        const emptyModules = emptyDigixModules();
        setCounts({ projects: 0, digixLeads: 0, customers: 0, accounts: 0, issues: 0 });
        setAdminStats({
          projectsNew: 0,
          projectsInProgress: 0,
          projectsActive: 0,
          projectsCompleted: 0,
          tasksTodo: 0,
          tasksInProgress: 0,
          subtasksTodo: 0,
          subtasksInProgress: 0,
          issuesOpen: 0,
          issuesInProgress: 0,
          issuesReopened: 0,
          digixModules: emptyModules,
          customersTotal: 0,
          followupsToday: 0,
          followupsOverdue: 0,
        });
        setDeveloperStats({
          assignedProjects: 0,
          assignedTasksTodo: 0,
          assignedTasksInProgress: 0,
          assignedIssuesOpen: 0,
          assignedIssuesInProgress: 0,
          assignedIssuesReopened: 0,
          assignedIssuesTotal: 0,
          assignedIssuesResolved: 0,
        });
        setTesterStats({
          assignedProjects: 0,
          assignedTasksTodo: 0,
          assignedTasksInProgress: 0,
          myIssuesOpen: 0,
          myIssuesInProgress: 0,
          myIssuesReopened: 0,
          myIssuesTotal: 0,
          myIssuesResolved: 0,
        });
        setDevopsStats({
          assignedProjects: 0,
          assignedTasksTodo: 0,
          assignedTasksInProgress: 0,
          assignedIssuesOpen: 0,
          assignedIssuesInProgress: 0,
          assignedIssuesReopened: 0,
          assignedIssuesTotal: 0,
          assignedIssuesResolved: 0,
          myIssuesOpen: 0,
          myIssuesInProgress: 0,
          myIssuesReopened: 0,
          myIssuesTotal: 0,
          myIssuesResolved: 0,
        });
        setHrStats({
          assignedTasksTodo: 0,
          assignedTasksInProgress: 0,
          assignedIssuesOpen: 0,
          assignedIssuesInProgress: 0,
          assignedIssuesReopened: 0,
          assignedIssuesTotal: 0,
        });
        setBaStats({ activeProjects: 0, createdUserStories: 0 });
        setFinanceStats({
          assignedTasksTodo: 0,
          assignedTasksInProgress: 0,
          assignedIssuesOpen: 0,
          assignedIssuesInProgress: 0,
          assignedIssuesReopened: 0,
          assignedIssuesTotal: 0,
          paymentsCompleted: 0,
          paymentsDue: 0,
        });
        setSalesStats({
          assignedTasksTodo: 0,
          assignedTasksInProgress: 0,
          assignedIssuesOpen: 0,
          assignedIssuesInProgress: 0,
          assignedIssuesReopened: 0,
          assignedIssuesTotal: 0,
          digixModules: emptyModules,
        });
        setRoleTasksOverviewRows([]);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!roleTasksCardExpanded) return;
    let mounted = true;
    const load = async () => {
      try {
        const res = await axiosInstance.get("/dashboard/assigned-tasks-overview");
        if (!mounted) return;
        const rows = res?.data?.assignedTasksOverview;
        setRoleTasksOverviewRows(Array.isArray(rows) ? rows : []);
      } catch {
        if (mounted) setRoleTasksOverviewRows([]);
      }
    };
    load();
    return () => { mounted = false; };
  }, [roleTasksCardExpanded]);

  useEffect(() => {
    if (isDeveloper || isTester || isDevops || showHrDashboard || showFinanceDashboard || showSalesDashboard) {
      return;
    }
    setRoleTasksCardExpanded(false);
    setRoleTasksOverviewRows([]);
    setDeveloperIssuesCardExpanded(false);
    setTesterIssuesCardExpanded(false);
    setHrIssuesCardExpanded(false);
    setFinanceIssuesCardExpanded(false);
    setSalesIssuesCardExpanded(false);
    setDevopsAssignedIssuesCardExpanded(false);
    setDevopsMyIssuesCardExpanded(false);
  }, [isDeveloper, isTester, isDevops, showHrDashboard, showFinanceDashboard, showSalesDashboard]);

  return (
    <div className="projects-container">
      <div className="main-card dashboard-page">
        <section className="dashboard-work-section" aria-label="Daily work">
          <div className="dashboard-work-tabs" role="tablist" aria-label="My Day and DM Calendar">
            <button
              type="button"
              role="tab"
              id="dashboard-tab-my-day"
              aria-selected={workTab === "my-day"}
              aria-controls="dashboard-panel-my-day"
              className={`dashboard-work-tab${workTab === "my-day" ? " is-active" : ""}`}
              onClick={() => setWorkTab("my-day")}
            >
              My Day
            </button>
            <button
              type="button"
              role="tab"
              id="dashboard-tab-dm"
              aria-selected={workTab === "dm"}
              aria-controls="dashboard-panel-dm"
              className={`dashboard-work-tab${workTab === "dm" ? " is-active" : ""}`}
              onClick={() => setWorkTab("dm")}
            >
              DM Calendar
            </button>
          </div>
          <div className="dashboard-work-select-wrap">
            <select
              className="dashboard-work-select"
              aria-label="Daily work view"
              value={workTab}
              onChange={(e) => setWorkTab(e.target.value)}
            >
              <option value="my-day">My Day</option>
              <option value="dm">DM Calendar</option>
            </select>
            <FaChevronDown className="dashboard-work-select-icon" aria-hidden="true" />
          </div>

          {workTab === "my-day" ? (
            <div
              id="dashboard-my-day"
              role="tabpanel"
              aria-labelledby="dashboard-tab-my-day"
              className="dashboard-work-panel dashboard-myday-layout"
            >
              <div className="dashboard-myday-main">
                <MyDay />
              </div>
              {isAdmin ? (
                <aside className="dashboard-myday-aside" aria-label="Overview">
                  <div className="dashboard-admin-summary-row dashboard-admin-summary-row--vertical">
              <div className="dashboard-summary-tile">
                <h3 className="dashboard-summary-tile__title">Projects</h3>
                <div className="dashboard-summary-tile__body">
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate("/projects", {
                        state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "new" },
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/projects", {
                          state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "new" },
                        });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">New</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.projectsNew}</span>
                  </div>
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate("/projects", {
                        state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "active" },
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/projects", {
                          state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "active" },
                        });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">Active</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.projectsInProgress}</span>
                  </div>
                </div>
              </div>
              <div className="dashboard-summary-tile">
                <h3 className="dashboard-summary-tile__title">Tasks</h3>
                <div className="dashboard-summary-tile__body">
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate("/tasks", {
                        state: { dashboardTaskStatusPreset: "todo" },
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/tasks", {
                          state: { dashboardTaskStatusPreset: "todo" },
                        });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">To do</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">
                      {Number(adminStats.tasksTodo || 0) + Number(adminStats.subtasksTodo || 0)}
                    </span>
                  </div>
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate("/tasks", {
                        state: { dashboardTaskStatusPreset: "inProgress" },
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/tasks", {
                          state: { dashboardTaskStatusPreset: "inProgress" },
                        });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">In progress</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">
                      {Number(adminStats.tasksInProgress || 0) + Number(adminStats.subtasksInProgress || 0)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="dashboard-summary-tile">
                <h3 className="dashboard-summary-tile__title">Issues</h3>
                <div className="dashboard-summary-tile__body">
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate("/issues", { state: { dashboardIssueStatusPreset: "open" } })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/issues", { state: { dashboardIssueStatusPreset: "open" } });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">Open</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.issuesOpen}</span>
                  </div>
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate("/issues", { state: { dashboardIssueStatusPreset: "inProgress" } })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/issues", { state: { dashboardIssueStatusPreset: "inProgress" } });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">In progress</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.issuesInProgress}</span>
                  </div>
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate("/issues", { state: { dashboardIssueStatusPreset: "reopened" } })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/issues", { state: { dashboardIssueStatusPreset: "reopened" } });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">Reopened</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.issuesReopened}</span>
                  </div>
                </div>
              </div>
              <div className="dashboard-summary-tile">
                <h3 className="dashboard-summary-tile__title">Customers</h3>
                <div className="dashboard-summary-tile__body">
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate("/tenants")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/tenants");
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">Total customers</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.customersTotal}</span>
                  </div>
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate("/tenants", {
                        state: { dashboardCustomerStatusPreset: "activeProjects" },
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/tenants", {
                          state: { dashboardCustomerStatusPreset: "activeProjects" },
                        });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">Active projects</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.projectsActive}</span>
                  </div>
                </div>
              </div>
              <div className="dashboard-summary-tile">
                <h3 className="dashboard-summary-tile__title">Follow-ups</h3>
                <div className="dashboard-summary-tile__body">
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setWorkTab("my-day");
                      requestAnimationFrame(() => {
                        document.getElementById("dashboard-my-day")?.scrollIntoView({ behavior: "smooth", block: "start" });
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setWorkTab("my-day");
                        requestAnimationFrame(() => {
                          document.getElementById("dashboard-my-day")?.scrollIntoView({ behavior: "smooth", block: "start" });
                        });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">Today</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.followupsToday}</span>
                  </div>
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setWorkTab("my-day");
                      requestAnimationFrame(() => {
                        document.getElementById("dashboard-my-day")?.scrollIntoView({ behavior: "smooth", block: "start" });
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setWorkTab("my-day");
                        requestAnimationFrame(() => {
                          document.getElementById("dashboard-my-day")?.scrollIntoView({ behavior: "smooth", block: "start" });
                        });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">Overdue</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.followupsOverdue}</span>
                  </div>
                </div>
              </div>
                  </div>
                </aside>
              ) : null}
            </div>
          ) : (
            <div
              id="dashboard-dm-calendar"
              role="tabpanel"
              aria-labelledby="dashboard-tab-dm"
              className="dashboard-work-panel"
            >
              <DMCalendar title="DM Calendar" />
            </div>
          )}
        </section>

        {workTab === "my-day" ? (
        <>
        {isDeveloper ? (
          <div className="dashboard-admin-stack">
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Projects</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() =>
                    navigate("/projects", {
                      state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "assigned" }
                    })
                  }
                >
                  <span className="dashboard-module-metric__label">Assigned Projects</span>
                  <span className="dashboard-module-metric__value">{developerStats.assignedProjects}</span>
                </button>
              </div>
            </div>
            <div
              className="dashboard-module-card"
              role="button"
              tabIndex={0}
              onClick={() => setRoleTasksCardExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRoleTasksCardExpanded((v) => !v);
                }
              }}
            >
              <h3 className="dashboard-module-card__title">Assigned Tasks</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/tasks", { state: { dashboardTaskStatusPreset: "todo" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">To-do</span>
                  <span className="dashboard-module-metric__value">{developerStats.assignedTasksTodo}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/tasks", { state: { dashboardTaskStatusPreset: "inProgress" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">In Progress</span>
                  <span className="dashboard-module-metric__value">{developerStats.assignedTasksInProgress}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Assigned Issues</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setDeveloperIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{developerStats.assignedIssuesTotal}</span>
                </button>
              </div>
            </div>
            {developerIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: developerStats.assignedIssuesOpen,
                    inProgress: developerStats.assignedIssuesInProgress,
                    reopened: developerStats.assignedIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            {roleTasksCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Tasks Overview"
              >
                <DashboardTasksOverviewTable rows={roleTasksOverviewRows} navigate={navigate} />
              </DashboardCollapsibleTableCard>
            ) : null}
          </div>
        ) : isTester ? (
          <div className="dashboard-admin-stack">
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Projects</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() =>
                    navigate("/projects", {
                      state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "assigned" }
                    })
                  }
                >
                  <span className="dashboard-module-metric__label">Assigned Projects</span>
                  <span className="dashboard-module-metric__value">{testerStats.assignedProjects}</span>
                </button>
              </div>
            </div>
            <div
              className="dashboard-module-card"
              role="button"
              tabIndex={0}
              onClick={() => setRoleTasksCardExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRoleTasksCardExpanded((v) => !v);
                }
              }}
            >
              <h3 className="dashboard-module-card__title">Assigned Tasks</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/tasks", { state: { dashboardTaskStatusPreset: "todo" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">To-do</span>
                  <span className="dashboard-module-metric__value">{testerStats.assignedTasksTodo}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/tasks", { state: { dashboardTaskStatusPreset: "inProgress" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">In Progress</span>
                  <span className="dashboard-module-metric__value">{testerStats.assignedTasksInProgress}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Issues Assigned by Me</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setTesterIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{testerStats.myIssuesTotal}</span>
                </button>
              </div>
            </div>
            {testerIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: testerStats.myIssuesOpen,
                    inProgress: testerStats.myIssuesInProgress,
                    reopened: testerStats.myIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            {roleTasksCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Tasks Overview"
              >
                <DashboardTasksOverviewTable rows={roleTasksOverviewRows} navigate={navigate} />
              </DashboardCollapsibleTableCard>
            ) : null}
          </div>
        ) : showHrDashboard ? (
          <div className="dashboard-admin-stack">
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">HR Team</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/hr-management", { state: { hrTab: "recruitment" } })}
                >
                  <span className="dashboard-module-metric__label">Recruitment</span>
                  <span className="dashboard-module-metric__value">—</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/hr-management", { state: { hrTab: "time" } })}
                >
                  <span className="dashboard-module-metric__label">Time management</span>
                  <span className="dashboard-module-metric__value">—</span>
                </button>
              </div>
            </div>
            <div
              className="dashboard-module-card"
              role="button"
              tabIndex={0}
              onClick={() => setRoleTasksCardExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRoleTasksCardExpanded((v) => !v);
                }
              }}
            >
              <h3 className="dashboard-module-card__title">Tasks</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/tasks", { state: { dashboardTaskStatusPreset: "todo" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">To-do</span>
                  <span className="dashboard-module-metric__value">{hrStats.assignedTasksTodo}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/tasks", { state: { dashboardTaskStatusPreset: "inProgress" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">In Progress</span>
                  <span className="dashboard-module-metric__value">{hrStats.assignedTasksInProgress}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Issues</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setHrIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{hrStats.assignedIssuesTotal}</span>
                </button>
              </div>
            </div>
            {hrIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: hrStats.assignedIssuesOpen,
                    inProgress: hrStats.assignedIssuesInProgress,
                    reopened: hrStats.assignedIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            {roleTasksCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Tasks Overview"
              >
                <DashboardTasksOverviewTable rows={roleTasksOverviewRows} navigate={navigate} />
              </DashboardCollapsibleTableCard>
            ) : null}
          </div>
        ) : showBaDashboard ? (
          <div className="dashboard-admin-stack">
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Projects</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/projects", { state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "active" } })}
                >
                  <span className="dashboard-module-metric__label">Active projects</span>
                  <span className="dashboard-module-metric__value">{baStats.activeProjects}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">User Stories</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/user-stories")}
                >
                  <span className="dashboard-module-metric__label">Created user stories</span>
                  <span className="dashboard-module-metric__value">{baStats.createdUserStories}</span>
                </button>
              </div>
            </div>
          </div>
        ) : showFinanceDashboard ? (
          <div className="dashboard-admin-stack">
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Finance Management</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/finance-management", { state: { financeTab: "invoices" } })}
                >
                  <span className="dashboard-module-metric__label">Invoices & Payments</span>
                  <span className="dashboard-module-metric__value">-</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/finance-management", { state: { financeTab: "tenants" } })}
                >
                  <span className="dashboard-module-metric__label">Tenants</span>
                  <span className="dashboard-module-metric__value">-</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/finance-management", { state: { financeTab: "accounts" } })}
                >
                  <span className="dashboard-module-metric__label">Company Accounts</span>
                  <span className="dashboard-module-metric__value">-</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/finance-management", { state: { financeTab: "customers" } })}
                >
                  <span className="dashboard-module-metric__label">Customer Accounts</span>
                  <span className="dashboard-module-metric__value">-</span>
                </button>
              </div>
            </div>
            <div
              className="dashboard-module-card"
              role="button"
              tabIndex={0}
              onClick={() => setRoleTasksCardExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRoleTasksCardExpanded((v) => !v);
                }
              }}
            >
              <h3 className="dashboard-module-card__title">Tasks</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/tasks", { state: { dashboardTaskStatusPreset: "todo" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">To-do</span>
                  <span className="dashboard-module-metric__value">{financeStats.assignedTasksTodo}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/tasks", { state: { dashboardTaskStatusPreset: "inProgress" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">In Progress</span>
                  <span className="dashboard-module-metric__value">{financeStats.assignedTasksInProgress}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Issues</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setFinanceIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{financeStats.assignedIssuesTotal}</span>
                </button>
              </div>
            </div>
            {financeIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: financeStats.assignedIssuesOpen,
                    inProgress: financeStats.assignedIssuesInProgress,
                    reopened: financeStats.assignedIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Payments</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() =>
                    navigate("/projects", {
                      state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "assigned" }
                    })
                  }
                >
                  <span className="dashboard-module-metric__label">Completed</span>
                  <span className="dashboard-module-metric__value">{financeStats.paymentsCompleted}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/projects", { state: { projectManagementTab: "Projects" } })}
                >
                  <span className="dashboard-module-metric__label">Due</span>
                  <span className="dashboard-module-metric__value">{financeStats.paymentsDue}</span>
                </button>
              </div>
            </div>
            {roleTasksCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Tasks Overview"
              >
                <DashboardTasksOverviewTable rows={roleTasksOverviewRows} navigate={navigate} />
              </DashboardCollapsibleTableCard>
            ) : null}
          </div>
        ) : showSalesDashboard ? (
          <div className="dashboard-admin-stack">
            <div
              className="dashboard-module-card"
              role="button"
              tabIndex={0}
              onClick={() => setRoleTasksCardExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRoleTasksCardExpanded((v) => !v);
                }
              }}
            >
              <h3 className="dashboard-module-card__title">Tasks</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/tasks", { state: { dashboardTaskStatusPreset: "todo" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">To-do</span>
                  <span className="dashboard-module-metric__value">{salesStats.assignedTasksTodo}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/tasks", { state: { dashboardTaskStatusPreset: "inProgress" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">In Progress</span>
                  <span className="dashboard-module-metric__value">{salesStats.assignedTasksInProgress}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Issues</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setSalesIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{salesStats.assignedIssuesTotal}</span>
                </button>
              </div>
            </div>
            {salesIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: salesStats.assignedIssuesOpen,
                    inProgress: salesStats.assignedIssuesInProgress,
                    reopened: salesStats.assignedIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            {roleTasksCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Tasks Overview"
              >
                <DashboardTasksOverviewTable rows={roleTasksOverviewRows} navigate={navigate} />
              </DashboardCollapsibleTableCard>
            ) : null}
          </div>
        ) : isDevops ? (
          <div className="dashboard-admin-stack">
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Projects</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/projects", { state: { projectManagementTab: "Projects" } })}
                >
                  <span className="dashboard-module-metric__label">Assigned Projects</span>
                  <span className="dashboard-module-metric__value">{devopsStats.assignedProjects}</span>
                </button>
              </div>
            </div>
            <div
              className="dashboard-module-card"
              role="button"
              tabIndex={0}
              onClick={() => setRoleTasksCardExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRoleTasksCardExpanded((v) => !v);
                }
              }}
            >
              <h3 className="dashboard-module-card__title">Assigned Tasks</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/tasks", { state: { dashboardTaskStatusPreset: "todo" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">To-do</span>
                  <span className="dashboard-module-metric__value">{devopsStats.assignedTasksTodo}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/tasks", { state: { dashboardTaskStatusPreset: "inProgress" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">In Progress</span>
                  <span className="dashboard-module-metric__value">{devopsStats.assignedTasksInProgress}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Assigned Issues</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setDevopsAssignedIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{devopsStats.assignedIssuesTotal}</span>
                </button>
              </div>
            </div>
            {devopsAssignedIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: devopsStats.assignedIssuesOpen,
                    inProgress: devopsStats.assignedIssuesInProgress,
                    reopened: devopsStats.assignedIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Issues Assigned by Me</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setDevopsMyIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{devopsStats.myIssuesTotal}</span>
                </button>
              </div>
            </div>
            {devopsMyIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: devopsStats.myIssuesOpen,
                    inProgress: devopsStats.myIssuesInProgress,
                    reopened: devopsStats.myIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            {roleTasksCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Tasks Overview"
              >
                <DashboardTasksOverviewTable rows={roleTasksOverviewRows} navigate={navigate} />
              </DashboardCollapsibleTableCard>
            ) : null}
          </div>
        ) : null}
          </>
        ) : null}

      </div>
    </div>
  );
}

export default Dashboard;
